/* eslint-disable @typescript-eslint/no-floating-promises */
import type { FriendVisitDetails, Visit } from '.';
import type { Pet } from '../buruburu/pet';

import {
  action,
  asyncAction,
  createActions,
  Replicant,
  ReplicantReadOnlyAPI,
  SB,
} from '@play-co/replicant';
import {
  getFriendVisitFinishEvent,
  getFriendVisitStartEvent,
  getHadVisitorChatbotPayload,
} from './visit.analytics';
import { getChatbotFlexMessage } from '../chatbot/flex/utils';
import { Locale } from '../ruleset/locale';
import { StripPromise } from '@play-co/replicant/lib/core/utils/TypeUtils';
import { getPSTDate } from '../utils/time';
import { scheduleStatusUpdateMessage } from '../buruburu/petChatbot/petChatbot.scheduledActions';
import { VisitRecord } from './record';
import { getCurrencyGrantEvent } from '../buruburu/inventory/inventory.ruleset';
import { Inventory } from '../buruburu/inventory';
import { Feature } from '../analytics';
import { sendChatbotMessage } from '../chatbot/chatbotMessage';

export async function asyncEndVisitAction(
  deleteVisitSession: () => void,
  state: Visit.State,
  api: ReplicantReadOnlyAPI<Replicant<{ state: Visit.State }>>,
  deps: {
    VisitRecord: Pick<VisitRecord, 'getters'>;
    Pet: Pick<Pet, 'getters'>;
  },
  sendMessage: boolean = true,
) {
  const { VisitRecord, Pet } = deps;

  // Get data from active session.
  const { friendId, careActions, coinsSpent, gemsReceived, friendDialogs } =
    state.visit.visitSession ?? {};
  if (!friendId) {
    throw new Error('No active visit session');
  }

  await api.flushMessages();

  // Fetch pet details.
  const petDetails: FriendVisitDetails = await api.asyncGetters
    .getPetVisitDetails(friendId)
    .catch((e) => {
      api.reportError(e);
      return null;
    });

  if (!petDetails) {
    // Make sure session is cleaned up regardless.
    deleteVisitSession();

    // Do not throw, but track the error.
    api.reportError(
      new Error(
        `Could not fetch details for friend ${friendId} while ending session`,
      ),
    );

    // Bail.
    return;
  }

  // Get relevant analytics data.
  const { getVisitedCount, getVisitsCount } = VisitRecord.getters;
  const visits = getVisitsCount(state, friendId);
  const visited = getVisitedCount(state, friendId);
  const {
    currentEvolution: { species },
  } = petDetails;

  // Send analytics event.
  const { level, age, attributes } = petDetails;
  api.sendAnalyticsEvents([
    getFriendVisitFinishEvent({
      friendID: friendId,
      friendPlayerLevel: level,
      friendPetID: species,
      friendAge: age,
      friendPetStomach: attributes.stomach,
      friendPetLove: attributes.love,
      friendDirtiness: attributes.dirtiness,
      friendUrine: attributes.urine,
      friendStress: attributes.stress,
      friendToothAcidity: attributes.toothAcidity,
      visits,
      visited,
      careActions,
      coinsSpent,
      gemsReceived,
      friendDialogs,
    }),
  ]);

  // Delete the session.
  deleteVisitSession();

  // Check that friend is offline before scheduling chatbot messages
  if (petDetails.isOnline) {
    return;
  }

  // Send had_visitor notification if friend has notifications turned on.
  if (petDetails.notificationSettings !== 'off' && sendMessage) {
    const profile = state.publicProfile ?? state.profile;
    const message = getChatbotFlexMessage({
      template: 'visited',
      payload: getHadVisitorChatbotPayload({
        friendId,
        friendPet: petDetails,
        visits,
        visited,
        isFriend: Object.keys(state.friends).includes(friendId),
      }),
      data: {
        locale: petDetails.locale as Locale,
        petName: petDetails.attributes.name,
        species: species,
        visitor: {
          name: profile.name,
          photo: profile.photo,
          pet: Pet.getters(state).getCurrentEvolution().species,
        },
      },
    });
    // api.chatbot.sendMessage(friendId, message);
    sendChatbotMessage(api, friendId, message, state.platform);
  }

  // Schedule status notifications for the friend's pet
  const nextPetState = await api.asyncGetters.getNextPetState(friendId);
  const now = api.date.now();
  scheduleStatusUpdateMessage({ nextPetState, now }, api, friendId);
}

export const visitActions = (
  Visit: Pick<
    Visit,
    | 'getters'
    | 'modifiers'
    | 'messages'
    | 'asyncGetters'
    | 'ruleset'
    | 'properties'
  >,
  {
    VisitRecord,
    Pet,
    PetStats,
    Encyclopedia,
    Inventory,
    Social,
    Profile,
    Fate,
  }: Visit['$DEPENDENCIES'],
) =>
  createActions({} as SB.Schema<Visit.MutableState>, {
    messages: { ...Visit.messages, ...Pet.messages },
    asyncGetters: {
      ...Visit.asyncGetters,
      ...Pet.asyncGetters,
    },
    computedProperties: {
      ...Visit.properties,
      ...Pet.properties,
      ...Social.properties,
      ...Profile.properties,
    },
  })({
    asyncBeginVisit: asyncAction(async (state, friendId: string, api) => {
      const petDetails = await api.asyncGetters.getPetVisitDetails(friendId);
      if (!petDetails) {
        throw new Error(`Pet not found for friend ${friendId}`);
      }

      if (!state.publicProfile && !state.friends[friendId]) {
        throw new Error(
          `Can't visit a non-friend without setting a public profile`,
        );
      }

      // Create new visit session
      state.visit.visitSession = {
        friendId,
        name: petDetails.name,
        petName: petDetails.attributes.name,
        currentEvolution: {
          species: petDetails.currentEvolution.species,
          stage: petDetails.currentEvolution.stage,
        },
        careActions: 0,
        friendDialogs: 0,
        gemsReceived: 0,
        coinsSpent: 0,
        answers: petDetails.answers,
        unsyncedChanges: {
          attributes: {},
          statusEffects: {},
        },
      };

      // Increment counter of visits to friend
      const now = api.date.now();
      const visits = VisitRecord.modifiers.incrementVisitsCount(
        state,
        friendId,
        now,
      );
      const visited = VisitRecord.getters.getVisitedCount(state, friendId);

      // Resolve 'want_to_go_out' effect.
      Fate.api.resolveStatusEffect(state, 'wantToGoOut', false, now);

      // Determine new fate
      Fate.api.update(state, now);

      // Post message to friend to track visit
      const { name } = state.publicProfile ?? state.profile;
      api.postMessage.trackVisit(friendId, { name });

      // Unveil pet in encyclopedia
      const { species } = petDetails.currentEvolution;
      if (Encyclopedia.getters(state).getSpeciesState(species) === 'veiled') {
        Encyclopedia.modifiers(state).setSpeciesState(species, 'unveiled');
      }

      // Clear any scheduled status messages for this friend
      api.scheduledActions.unschedule(
        `sendStatusChangedChatbotMessage#${friendId}`,
      );

      // Send analytics
      const { level, age, attributes } = petDetails;
      api.sendAnalyticsEvents([
        getFriendVisitStartEvent({
          friendID: friendId,
          friendPlayerLevel: level,
          friendPetID: species,
          friendAge: age,
          friendPetStomach: attributes.stomach,
          friendPetLove: attributes.love,
          friendDirtiness: attributes.dirtiness,
          friendUrine: attributes.urine,
          friendStress: attributes.stress,
          friendToothAcidity: attributes.toothAcidity,
          visits,
          visited,
        }),
      ]);

      return petDetails;
    }),

    asyncEndVisit: asyncAction((state, sendMessage: boolean, api) =>
      asyncEndVisitAction(
        () => delete state.visit.visitSession,
        state,
        api,
        {
          VisitRecord,
          Pet,
        },
        sendMessage,
      ),
    ),

    asyncSyncFriendState: asyncAction(async (state, _, api) => {
      if (!state.visit.visitSession) {
        throw new Error(`No active visit session`);
      }

      const { friendId, unsyncedChanges } = state.visit.visitSession;
      const oldPetDetails = await api.asyncGetters.getPetVisitDetails(friendId);

      if (!oldPetDetails) {
        throw new Error(`Pet not found for friend ${friendId}`);
      }

      if (!state.publicProfile && !state.friends[friendId]) {
        throw new Error(
          `Can't visit a non-friend without setting a public profile`,
        );
      }

      api.postMessage.updateFate(friendId, {
        now: api.date.now(),
        effects: unsyncedChanges.statusEffects,
        attributes: unsyncedChanges.attributes,
      });

      await api.flushMessages();

      // eslint-disable-next-line require-atomic-updates
      state.visit.visitSession.unsyncedChanges = {
        attributes: {},
        statusEffects: {},
      };

      const petDetails = await api.asyncGetters.getPetVisitDetails(friendId);

      // Pet was already happy or didn't become happy, so no gems are granted.
      if (oldPetDetails.state === 'happy' || petDetails.state !== 'happy') {
        return { granted: false, petDetails };
      }

      // Get JST date for today.
      const jstDay = getPSTDate(api.date.now());
      // Shift date to beggining of JST day.
      jstDay.setHours(0, 0, 0, 0);

      const { visits, lastGemsGrantAt } = state.visit;
      const { lastGemsGrantAt: lastFriendGemsGrantAt } = visits[friendId] ?? {};

      // Get friend's last gem grant JST date.
      const lastGrantFromFriend = getPSTDate(lastFriendGemsGrantAt);
      // Shift date to beggining of JST day.
      lastGrantFromFriend.setHours(0, 0, 0, 0);

      // Can only get gems once per day. So if dates are the same, no gems are granted.
      if (jstDay.getTime() === lastGrantFromFriend.getTime()) {
        return { granted: false, petDetails };
      }

      // Get last gem grant JST date.
      const lastGrant = getPSTDate(lastGemsGrantAt);
      // Shift date to beggining of JST day.
      lastGrantFromFriend.setHours(0, 0, 0, 0);

      // Reset counter if it's been more than a day since last grant.
      if (jstDay.getTime() > lastGrant.getTime()) {
        state.visit.gemsGrantsToday = 0;
      }

      // Get a random number.
      const random = api.math.random();
      // Chance decreases by 20% for every grant of the day.
      const chance = Math.max(0, 1 - state.visit.gemsGrantsToday * 0.2);

      // If roll is successful, grant gems.
      if (random >= chance) {
        return { granted: false, petDetails };
      }

      const { gemsReward } = Visit.ruleset;

      // Update visit state.
      Visit.modifiers(state).incrementGemsGrants(friendId, api.date.now());

      // Add pending gems.
      state.visit.pendingGemsGrants += gemsReward;

      return { granted: true, petDetails };
    }),

    claimVisitGems: action((state, _, api) => {
      const { pendingGemsGrants } = state.visit;

      const now = api.date.now();
      const { getCurrencies } = Inventory.getters(state);
      const oldCurrencies = getCurrencies(now);

      Inventory.modifiers(state).addFreeGems(pendingGemsGrants);
      state.visit.pendingGemsGrants = 0;

      api.sendAnalyticsEvents([
        getCurrencyGrantEvent(
          { feature: Feature.VISIT._, $subFeature: Feature.VISIT.REWARD },
          { old: oldCurrencies, new: getCurrencies(now) },
        ),
      ]);
    }),

    acknowledgeVisit: action((state, _: void, api) => {
      const now = api.date.now();
      Fate.api.resolveStatusEffect(state, 'hadVisitor', false, now);
      Fate.api.update(state, now);
    }),

    kickVisitors: action((state, visitors: string[], api) => {
      visitors?.forEach((id) => api.postMessage.kickVisitor(id, {}));
    }),
  });

export type GrantGemsReturn = StripPromise<
  ReturnType<ReturnType<typeof visitActions>['asyncSyncFriendState']['fn']>
>;
